// Tela de Tags no padrão Aeros Dark (mockup "Tela Aeros Dark - Tags e
// Modais"): uma lista só com tags de contato e de Kanban (GET /tags?mode=aeros
// com contagens por subquery), filtro Todas/Kanban/Sem uso, paginação
// numerada e modais Aeros (tag, contatos da tag, excluir, excluir todas).
import React, { useContext, useEffect, useMemo, useReducer, useState } from "react";
import { toast } from "react-toastify";
import { useHistory, useLocation } from "react-router-dom";
import { makeStyles, alpha } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import InputBase from "@material-ui/core/InputBase";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import GroupIcon from "@material-ui/icons/Group";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import DeleteSweepIcon from "@material-ui/icons/DeleteSweep";
import TimerIcon from "@material-ui/icons/Timer";
import ViewColumnIcon from "@material-ui/icons/ViewColumn";
import LocalOfferOutlinedIcon from "@material-ui/icons/LocalOfferOutlined";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

import MainContainer from "../../components/MainContainer";
import TagModal from "../../components/TagModal";
import TagContactsModal from "../../components/TagContactsModal";
import AerosConfirmModal from "../../components/AerosConfirmModal";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { avatarColorFor, initialsOf, totalPages, pageWindow } from "../Contacts/contactRules";
import { FILTERS, tagChip, kanbanCount, recurrenceLabel } from "./tagRules";

const PAGE_SIZE = 20;
const TABLE_GRID = "2fr 1.1fr 1fr 1fr 1.2fr 1.3fr 1.2fr";

const reducer = (state, action) => {
  if (action.type === "LOAD") return action.payload;
  if (action.type === "UPSERT") {
    const i = state.findIndex((t) => t.id === action.payload.id);
    if (i === -1) return state;
    const next = [...state];
    next[i] = { ...next[i], ...action.payload };
    return next;
  }
  if (action.type === "DELETE") return state.filter((t) => t.id !== action.payload);
  if (action.type === "RESET") return [];
  return state;
};

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const border = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  const borderStrong = isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.14)";
  const cyanText = stateText(theme, AEROS.cyan, "info");
  const purpleText = stateText(theme, "#C08BFF", "purple");
  const errorText = stateText(theme, AEROS.error, "error");
  return {
    pageHeader: {
      minHeight: 60,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      gap: 16,
      padding: "0 28px",
      borderBottom: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.06)"}`,
      [theme.breakpoints.down("sm")]: { padding: "8px 12px", flexWrap: "wrap" },
    },
    pageTitle: { fontFamily: AEROS.fontDisplay, fontSize: 16, fontWeight: 700, letterSpacing: "-0.02em", color: theme.palette.text.primary, lineHeight: 1.3 },
    pageTitleCount: { color: theme.palette.text.secondary, fontWeight: 500 },
    pageSubtitle: { fontSize: 12, color: theme.palette.text.secondary, lineHeight: 1.3 },
    headerActions: { marginLeft: "auto", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
    searchPill: {
      width: 220,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)"}`,
      borderRadius: 10,
      padding: "5px 14px",
      fontSize: 13,
      color: theme.palette.text.primary,
      display: "flex",
      alignItems: "center",
      gap: 8,
      "& .MuiSvgIcon-root": { fontSize: 18, color: theme.palette.text.secondary },
      "& input::placeholder": { color: theme.palette.text.secondary, opacity: 1 },
    },
    segmented: { display: "flex", border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)"}`, borderRadius: 10, overflow: "hidden", fontSize: 12, fontWeight: 600 },
    segment: { padding: "8px 14px", color: theme.palette.text.secondary, background: "transparent", border: "none", cursor: "pointer", font: "inherit", fontWeight: 600, "&:hover": { color: theme.palette.text.primary } },
    segmentActive: { backgroundColor: "rgba(160,68,255,0.2)", color: purpleText },
    dangerButton: {
      border: `1px solid ${alpha(AEROS.error, 0.4)}`,
      backgroundColor: alpha(AEROS.error, 0.08),
      color: errorText,
      borderRadius: 10,
      padding: "7px 14px",
      fontSize: 13,
      fontWeight: 600,
      "& .MuiButton-startIcon .MuiSvgIcon-root": { fontSize: 17 },
      "&:hover": { backgroundColor: AEROS.error, color: "#07080D" },
    },
    gradientButton: { borderRadius: 10, padding: "7px 16px", fontSize: 13, fontWeight: 700, "& .MuiSvgIcon-root": { fontSize: 17 } },
    pageBody: { flex: 1, padding: "24px 28px", overflowY: "auto", ...theme.scrollbarStyles, [theme.breakpoints.down("sm")]: { padding: "16px 12px" } },
    tableCard: { backgroundColor: theme.palette.aeros.surface, border: `1px solid ${border}`, borderRadius: 14, overflow: "hidden" },
    tableHeadRow: {
      display: "grid",
      gridTemplateColumns: TABLE_GRID,
      alignItems: "center",
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
      backgroundColor: isLight ? "rgba(15,20,40,0.02)" : "rgba(255,255,255,0.02)",
      borderBottom: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`,
      "& > div": { padding: "12px 8px" },
      "& > div:first-child": { paddingLeft: 20 },
    },
    tableRow: {
      display: "grid",
      gridTemplateColumns: TABLE_GRID,
      alignItems: "center",
      fontSize: 13.5,
      fontVariantNumeric: "tabular-nums",
      color: theme.palette.text.primary,
      borderBottom: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.05)"}`,
      "&:hover": { backgroundColor: "rgba(160,68,255,0.04)" },
      "& > div": { padding: "10px 8px", minWidth: 0 },
      "& > div:first-child": { paddingLeft: 20 },
    },
    center: { textAlign: "center" },
    secondary: { color: theme.palette.text.secondary },
    tagCell: { display: "flex", alignItems: "center", gap: 10 },
    swatch: { width: 10, height: 10, borderRadius: 3, flex: "none" },
    chip: { display: "inline-flex", alignItems: "center", fontSize: 12, fontWeight: 600, borderRadius: 6, padding: "3px 10px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
    timer: { fontSize: 15, color: AEROS.warning },
    kindPill: { display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10.5, fontWeight: 600, borderRadius: 99, padding: "2px 9px", whiteSpace: "nowrap", "& .MuiSvgIcon-root": { fontSize: 12 } },
    kindKanban: { color: purpleText, backgroundColor: "rgba(160,68,255,0.12)", border: "1px solid rgba(160,68,255,0.35)" },
    kindContact: { color: theme.palette.text.secondary, border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.12)"}` },
    rec: { fontSize: 12.5 },
    recNone: { color: theme.palette.aeros.textTertiary },
    owner: { display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: theme.palette.text.secondary },
    ownerAvatar: { width: 22, height: 22, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, fontWeight: 800, color: "#07080D", flex: "none" },
    actions: { display: "flex", justifyContent: "flex-end", gap: 6, paddingRight: "20px !important" },
    actionsHead: { textAlign: "right", paddingRight: "20px !important" },
    rowActionButton: {
      width: 30,
      height: 30,
      borderRadius: 8,
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`,
      color: theme.palette.text.secondary,
      padding: 0,
      "&:hover": { color: cyanText, borderColor: alpha(AEROS.cyan, 0.4), backgroundColor: "transparent" },
      "& .MuiSvgIcon-root": { fontSize: 16 },
    },
    rowActionDelete: { "&:hover": { color: errorText, borderColor: alpha(AEROS.error, 0.4), backgroundColor: "transparent" } },
    tableFooter: { display: "flex", alignItems: "center", gap: 12, padding: "12px 20px", fontSize: 12, color: theme.palette.text.secondary },
    pager: { marginLeft: "auto", display: "flex", gap: 4 },
    pageButton: {
      width: 28, height: 28, minWidth: 28, padding: 0, borderRadius: 8,
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.1)"}`,
      fontSize: 12, fontWeight: 600, color: theme.palette.text.primary,
      "& .MuiSvgIcon-root": { fontSize: 16 },
      "&.Mui-disabled": { opacity: 0.4 },
    },
    pageButtonActive: { backgroundColor: "rgba(160,68,255,0.2)", color: purpleText, borderColor: "transparent", fontWeight: 700 },
    emptyRow: { padding: "36px 0", textAlign: "center", fontSize: 12.5, color: theme.palette.aeros.textTertiary },
    warnBox: { marginTop: 12, display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: stateText(theme, AEROS.warning, "warning"), backgroundColor: alpha(AEROS.warning, 0.08), border: `1px solid ${alpha(AEROS.warning, 0.25)}`, borderRadius: 9, padding: "8px 11px", "& .MuiSvgIcon-root": { fontSize: 15 } },
    strong: { color: theme.palette.text.primary, fontWeight: 600 },
    borderStrong,
  };
});

const Tags = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { user, socket } = useContext(AuthContext);
  const isAdmin = user.profile === "admin";

  const [tags, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [searchParam, setSearchParam] = useState("");
  const [filtro, setFiltro] = useState(() => {
    const f = new URLSearchParams(location.search).get("filtro");
    return FILTERS.includes(f) ? f : "all";
  });
  const [reloadTick, setReloadTick] = useState(0);
  const [kanbanTotal, setKanbanTotal] = useState(0);

  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [selectedTagId, setSelectedTagId] = useState(null);
  const [contactsTag, setContactsTag] = useState(null);
  const [deletingTag, setDeletingTag] = useState(null);
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [searchParam, filtro]);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/tags/", {
          params: { mode: "aeros", filtro, searchParam, pageNumber: page, limit: PAGE_SIZE },
        });
        dispatch({ type: "LOAD", payload: data.tags });
        setCount(data.count);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [searchParam, filtro, page, reloadTick]);

  // Subtítulo "· K usadas no Kanban" conta a empresa toda, não só a página.
  useEffect(() => {
    api
      .get("/tags/", { params: { mode: "aeros", filtro: "kanban", pageNumber: 1, limit: 1 } })
      .then(({ data }) => setKanbanTotal(data.count))
      .catch(() => setKanbanTotal(0));
  }, [reloadTick]);

  useEffect(() => {
    const onTag = (data) => {
      if (data.action === "update" || data.action === "create" || data.action === "delete" || data.action === "delete-all") {
        setReloadTick((n) => n + 1);
      }
    };
    socket.on(`company${user.companyId}-tag`, onTag);
    return () => socket.off(`company${user.companyId}-tag`, onTag);
  }, [socket, user.companyId]);

  const reload = () => setReloadTick((n) => n + 1);

  const handleDelete = async () => {
    if (!deletingTag) return;
    setConfirming(true);
    try {
      await api.delete(`/tags/${deletingTag.id}`);
      toast.success(i18n.t("tags.toasts.deleted"));
      dispatch({ type: "DELETE", payload: deletingTag.id });
      setDeletingTag(null);
      setTagModalOpen(false);
      reload();
    } catch (err) {
      toastError(err);
    } finally {
      setConfirming(false);
    }
  };

  const handleDeleteAll = async () => {
    setConfirming(true);
    try {
      const { data } = await api.delete("/tags/all", { data: { confirmation: "DELETE_ALL_TAGS" } });
      toast.success(i18n.t("tags.toasts.deletedAll", { count: data.deleted }));
      setDeleteAllOpen(false);
      reload();
    } catch (err) {
      toastError(err);
    } finally {
      setConfirming(false);
    }
  };

  const pages = totalPages(count, PAGE_SIZE);
  const pageKanban = useMemo(() => kanbanCount(tags), [tags]);

  return (
    <MainContainer>
      <TagModal
        open={tagModalOpen}
        tagId={selectedTagId}
        onClose={() => {
          setTagModalOpen(false);
          setSelectedTagId(null);
        }}
        onSaved={reload}
        onDelete={(tag) => setDeletingTag(tag)}
      />
      <TagContactsModal open={Boolean(contactsTag)} tag={contactsTag} onClose={() => setContactsTag(null)} onChanged={reload} />
      <AerosConfirmModal
        open={Boolean(deletingTag)}
        onClose={() => setDeletingTag(null)}
        onConfirm={handleDelete}
        loading={confirming}
        icon={<DeleteOutlineIcon />}
        title={
          deletingTag && (
            <>
              {i18n.t("tags.confirmDelete.titleBefore")}{" "}
              <span className={classes.chip} style={{ color: tagChip(deletingTag).color, backgroundColor: tagChip(deletingTag).bg, verticalAlign: "middle" }}>
                {deletingTag.name}
              </span>
              ?
            </>
          )
        }
        description={
          deletingTag && (
            <>
              {i18n.t("tags.confirmDelete.textBefore")} <b className={classes.strong}>{Number(deletingTag.contactsCount || 0)} {i18n.t("tags.confirmDelete.contacts")}</b>{" "}
              {i18n.t("tags.confirmDelete.and")} <b className={classes.strong}>{Number(deletingTag.ticketsCount || 0)} {i18n.t("tags.confirmDelete.tickets")}</b>. {i18n.t("tags.confirmDelete.textAfter")}
              {Number(deletingTag.kanban) === 1 && (
                <div className={classes.warnBox}>
                  <TimerIcon /> {i18n.t("tags.confirmDelete.kanbanWarn")}
                </div>
              )}
            </>
          )
        }
        confirmLabel={i18n.t("tags.confirmDelete.confirm")}
      />
      <AerosConfirmModal
        open={deleteAllOpen}
        onClose={() => setDeleteAllOpen(false)}
        onConfirm={handleDeleteAll}
        loading={confirming}
        icon={<DeleteSweepIcon />}
        title={i18n.t("tags.confirmDeleteAll.title", { count })}
        description={i18n.t("tags.confirmDeleteAll.text")}
        confirmLabel={i18n.t("tags.confirmDeleteAll.confirm")}
        typedWord="EXCLUIR"
      />

      <div className={classes.pageHeader}>
        <div>
          <Typography component="div" className={classes.pageTitle}>
            {i18n.t("tags.title")} <span className={classes.pageTitleCount}>· {count}</span>
          </Typography>
          <div className={classes.pageSubtitle}>{i18n.t("tags.subtitle", { kanban: kanbanTotal })}</div>
        </div>
        <div className={classes.headerActions}>
          <InputBase
            className={classes.searchPill}
            placeholder={i18n.t("tags.search")}
            type="search"
            value={searchParam}
            onChange={(e) => setSearchParam(e.target.value.toLowerCase())}
            startAdornment={<SearchIcon />}
            inputProps={{ "aria-label": i18n.t("tags.search") }}
          />
          <div className={classes.segmented} role="tablist">
            {FILTERS.map((f) => (
              <button
                key={f}
                type="button"
                role="tab"
                aria-selected={filtro === f}
                className={`${classes.segment} ${filtro === f ? classes.segmentActive : ""}`}
                onClick={() => {
                  setFiltro(f);
                  history.replace(f === "all" ? "/tags" : `/tags?filtro=${f}`);
                }}
              >
                {i18n.t(`tags.filters.${f}`)}
              </button>
            ))}
          </div>
          {isAdmin && (
            <Button className={classes.dangerButton} startIcon={<DeleteSweepIcon />} onClick={() => setDeleteAllOpen(true)} disabled={count === 0}>
              {i18n.t("tags.buttons.deleteAll")}
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            className={classes.gradientButton}
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedTagId(null);
              setTagModalOpen(true);
            }}
          >
            {i18n.t("tags.buttons.add")}
          </Button>
        </div>
      </div>

      <div className={classes.pageBody}>
        <div className={classes.tableCard}>
          <div className={classes.tableHeadRow}>
            <div>{i18n.t("tags.table.tag")}</div>
            <div>{i18n.t("tags.table.type")}</div>
            <div className={classes.center}>{i18n.t("tags.table.contacts")}</div>
            <div className={classes.center}>{i18n.t("tags.table.tickets")}</div>
            <div>{i18n.t("tags.table.recurrence")}</div>
            <div>{i18n.t("tags.table.createdBy")}</div>
            <div className={classes.actionsHead}>{i18n.t("tags.table.actions")}</div>
          </div>

          {tags.map((tag) => {
            const chip = tagChip(tag);
            const isKanban = Number(tag.kanban) === 1;
            const rec = recurrenceLabel(tag.recurrenceDays);
            return (
              <div key={tag.id} className={classes.tableRow}>
                <div className={classes.tagCell}>
                  <span className={classes.swatch} style={{ backgroundColor: chip.color }} />
                  <span className={classes.chip} style={{ color: chip.color, backgroundColor: chip.bg }} title={tag.name}>
                    {tag.name}
                  </span>
                  {isKanban && Number(tag.timeLane) > 0 && (
                    <Tooltip title={i18n.t("tags.table.timeAlert")}>
                      <TimerIcon className={classes.timer} />
                    </Tooltip>
                  )}
                </div>
                <div>
                  <span className={`${classes.kindPill} ${isKanban ? classes.kindKanban : classes.kindContact}`}>
                    {isKanban ? <ViewColumnIcon /> : <LocalOfferOutlinedIcon />}
                    {i18n.t(isKanban ? "tags.table.kindKanban" : "tags.table.kindContact")}
                  </span>
                </div>
                <div className={classes.center}>{Number(tag.contactsCount || 0)}</div>
                <div className={`${classes.center} ${classes.secondary}`}>{Number(tag.ticketsCount || 0)}</div>
                <div className={`${classes.rec} ${rec ? "" : classes.recNone}`}>{rec || "—"}</div>
                <div className={classes.owner}>
                  {tag.user ? (
                    <>
                      <span className={classes.ownerAvatar} style={{ backgroundColor: avatarColorFor(tag.user.id) }}>
                        {initialsOf(tag.user.name)}
                      </span>
                      {tag.user.name.split(" ")[0]}
                    </>
                  ) : (
                    <span className={classes.recNone}>—</span>
                  )}
                </div>
                <div className={classes.actions}>
                  <Tooltip title={i18n.t("tags.table.seeContacts")}>
                    <IconButton size="small" className={classes.rowActionButton} onClick={() => setContactsTag(tag)} aria-label={i18n.t("tags.table.seeContacts")}>
                      <GroupIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={i18n.t("tags.table.edit")}>
                    <IconButton
                      size="small"
                      className={classes.rowActionButton}
                      onClick={() => {
                        setSelectedTagId(tag.id);
                        setTagModalOpen(true);
                      }}
                      aria-label={i18n.t("tags.table.edit")}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={i18n.t("tags.table.delete")}>
                    <IconButton size="small" className={`${classes.rowActionButton} ${classes.rowActionDelete}`} onClick={() => setDeletingTag(tag)} aria-label={i18n.t("tags.table.delete")}>
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            );
          })}

          {loading && (
            <div className={classes.emptyRow}>
              <CircularProgress size={22} />
            </div>
          )}
          {!loading && tags.length === 0 && <div className={classes.emptyRow}>{i18n.t("tags.empty")}</div>}

          <div className={classes.tableFooter}>
            {i18n.t("tags.showing", { shown: tags.length, total: count })}
            {pageKanban > 0 && filtro === "all" ? "" : ""}
            {pages > 1 && (
              <div className={classes.pager}>
                <IconButton className={classes.pageButton} disabled={page <= 1} onClick={() => setPage(page - 1)} aria-label={i18n.t("contacts.pagination.prev")}>
                  <ChevronLeftIcon />
                </IconButton>
                {pageWindow(page, pages).map((p) => (
                  <Button key={p} className={`${classes.pageButton} ${p === page ? classes.pageButtonActive : ""}`} onClick={() => setPage(p)} aria-current={p === page ? "page" : undefined}>
                    {p}
                  </Button>
                ))}
                <IconButton className={classes.pageButton} disabled={page >= pages} onClick={() => setPage(page + 1)} aria-label={i18n.t("contacts.pagination.next")}>
                  <ChevronRightIcon />
                </IconButton>
              </div>
            )}
          </div>
        </div>
      </div>
    </MainContainer>
  );
};

export default Tags;
